import axios from 'axios';
import { getServerUrl } from '../utils/connectionStatus';
import { notifyBackendSessionChanged } from '../lib/appMode';
import { getDeviceId } from '../utils/deviceFingerprint';
import {
  offlineRequestInterceptor,
  offlineResponseErrorInterceptor,
} from '../lib/offlineMutationQueue';
import { maybeNotifyOfflineQueuedResponse } from '../lib/offlineMutationFeedback';

// Use dynamic server URL for LAN support
const API_BASE_URL = getServerUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add device ID to all requests
api.interceptors.request.use((config) => {
  const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
  const isUrlParams = typeof URLSearchParams !== 'undefined' && config.data instanceof URLSearchParams;
  if (config.data != null && !isFormData && !isUrlParams && typeof config.data !== 'string') {
    config.headers['Content-Type'] = 'application/json';
  }

  let deviceId = 'unknown';
  try {
    deviceId = getDeviceId();
  } catch {
    deviceId = localStorage.getItem('deviceId') || localStorage.getItem('device_id') || 'unknown';
  }
  config.headers['x-device-id'] = deviceId;

  // Express LAN desktop session. Tab-only login keeps sessionId in sessionStorage.
  const sessionId =
    sessionStorage.getItem('sessionId') || localStorage.getItem('sessionId');
  if (sessionId) {
    config.headers['x-session-id'] = sessionId;
  }
  config._zbHadBackendSession = !!sessionId;

  const shopId =
    localStorage.getItem('zb_active_shop_id') || sessionStorage.getItem('zb_active_shop_id');
  if (shopId) {
    config.headers['x-shop-id'] = shopId;
  }

  const profileId =
    localStorage.getItem('zb_simple_uid') ||
    sessionStorage.getItem('zb_simple_uid') ||
    localStorage.getItem('zb_pending_uid') ||
    sessionStorage.getItem('zb_pending_uid');
  if (profileId && /^[0-9a-f-]{36}$/i.test(String(profileId).trim())) {
    config.headers['x-zb-profile-id'] = String(profileId).trim();
  }

  return config;
});

api.interceptors.request.use(
  (config) => offlineRequestInterceptor(config),
  (err) => Promise.reject(err)
);

// Add response interceptor to handle read-only errors
// CRITICAL: Prevent infinite redirect loops
let isRedirecting = false;
api.interceptors.response.use(
  (response) => {
    maybeNotifyOfflineQueuedResponse(response);
    return response;
  },
  async (error) => {
    try {
      const queued = await offlineResponseErrorInterceptor(error);
      if (queued && queued.status === 202 && queued.data?.offlineQueued) {
        maybeNotifyOfflineQueuedResponse(queued);
        return queued;
      }
    } catch {
      /* ignore */
    }
    if (error.response?.status === 401) {
      // Only redirect when a backend session was sent but rejected (expired/invalid).
      // Supabase-only users have no sessionId — API 401 must not force /login or we get an infinite loop
      // (React still sees zb_simple_* session → /shops → /app → 401 → /login → …).
      const hadBackendSession = error.config?._zbHadBackendSession === true;
      if (
        hadBackendSession &&
        !isRedirecting &&
        window.location.pathname !== '/login'
      ) {
        isRedirecting = true;
        localStorage.removeItem('sessionId');
        sessionStorage.removeItem('sessionId');
        localStorage.removeItem('user');
        notifyBackendSessionChanged();
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }
    if (error.response?.data?.readOnly) {
      console.warn('Read-only mode: Operation blocked');
    }
    return Promise.reject(error);
  }
);

// Products API
export const productsAPI = {
  getAll: () => api.get('/products'),
  getById: (id) => api.get(`/products/${id}`),
  getActivity: (id) => api.get(`/products/${id}/activity`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  importBulk: (body) => api.post('/products/import-bulk', body),
};

// Suppliers API
export const suppliersAPI = {
  getAll: () => api.get('/suppliers'),
  getById: (id) => api.get(`/suppliers/${id}`),
  getLedger: (id) => api.get(`/suppliers/${id}/ledger`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Supplier Payments API
export const supplierPaymentsAPI = {
  getAll: (params) => api.get('/supplier-payments', { params }),
  getById: (id) => api.get(`/supplier-payments/${id}`),
  create: (data) => api.post('/supplier-payments', data),
  update: (id, data) => api.put(`/supplier-payments/${id}`, data),
  delete: (id) => api.delete(`/supplier-payments/${id}`),
};

// Sales API
export const salesAPI = {
  getAll: (params) => api.get('/sales', { params }),
  getById: (id) => api.get(`/sales/${id}`),
  nextInvoiceNumber: () => api.get('/sales/next-invoice-number'),
  create: (data) => api.post('/sales', data),
  update: (id, data) => api.put(`/sales/${id}`, data),
  delete: (id) => api.delete(`/sales/${id}`),
  createReturn: (id, data) => api.post(`/sales/${id}/return`, data),
  print: (id) => api.get(`/sales/${id}/print`),
};

export const returnsAPI = {
  getAll: (params) => api.get('/returns', { params }),
  getById: (id) => api.get(`/returns/${id}`),
};

export const dailyClosingAPI = {
  todaySummary: () => api.get('/daily-closing/today-summary'),
  close: (body) => api.post('/daily-closing/close', body),
};

// Reports API
export const reportsAPI = {
  getComprehensive: (period, productId, supplierId, startDate = null, endDate = null) => {
    let url = `/reports/comprehensive?period=${period}`;
    if (productId) url += `&productId=${productId}`;
    if (supplierId) url += `&supplierId=${supplierId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    return api.get(url);
  },
  getProducts: () => api.get('/reports/products'),
  getSuppliers: () => api.get('/reports/suppliers'),
  getStock: () => api.get('/reports/stock'),
  getCustomersOutstanding: () => api.get('/reports/customers-outstanding'),
  getSuppliersPayable: () => api.get('/reports/suppliers-payable'),
  getCustomersDue: (params) => api.get('/reports/customers-due', { params }),
  // New endpoints
  getDashboardSummary: (params) => api.get('/reports/dashboard-summary', { params }),
  getSalesSummary: (params) => api.get('/reports/sales-summary', { params }),
  getSalesInvoices: (params) => api.get('/reports/sales-invoices', { params }),
  getCustomersAnalytics: (params) => api.get('/reports/customers-analytics', { params }),
  getSuppliersAnalytics: (params) => api.get('/reports/suppliers-analytics', { params }),
  getSalesByProduct: (params) => api.get('/reports/sales-by-product', { params }),
  getProfit: (params) => api.get('/reports/profit', { params }),
  getExpensesSummary: (params) => api.get('/reports/expenses-summary', { params }),
  getExpensesList: (params) => api.get('/reports/expenses-list', { params }),
  getStockCurrent: () => api.get('/reports/stock-current'),
  getStockLow: (params) => api.get('/reports/stock-low', { params }),
  getCustomerStatement: (id, params) => api.get(`/reports/customer-statement/${id}`, { params }),
  getSupplierHistory: (id, params) => api.get(`/reports/supplier-history/${id}`, { params }),
  getDashboard: () => api.get('/reports/dashboard'),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Customers API
export const customersAPI = {
  getAll: () => api.get('/customers'),
  getById: (id) => api.get(`/customers/${id}`),
  getHistory: (id) => api.get(`/customers/${id}/history`),
  getLedger: (id) => api.get(`/customers/${id}/ledger`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  // Sub-categories
  getSubCategoriesAll: () => api.get('/categories/sub-categories/all'),
  createSubCategory: (data) => api.post('/categories/sub-categories', data),
  updateSubCategory: (id, data) => api.put(`/categories/sub-categories/${id}`, data),
  deleteSubCategory: (id) => api.delete(`/categories/sub-categories/${id}`),
};

// Units of Measure API
export const unitsAPI = {
  getAll: () => api.get('/units'),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  delete: (id) => api.delete(`/units/${id}`),
};

// Purchases API
export const purchasesAPI = {
  getAll: () => api.get('/purchases'),
  getById: (id) => api.get(`/purchases/${id}`),
  create: (data) => api.post('/purchases', data),
  update: (id, data) => api.put(`/purchases/${id}`, data),
  delete: (id) => api.delete(`/purchases/${id}`),
};

// Expenses API
export const expensesAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  getSummary: (params) => api.get('/expenses/summary', { params }),
  getMonthly: () => api.get('/expenses/monthly'),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// Customer Payments API
export const customerPaymentsAPI = {
  getAll: (params) => api.get('/customer-payments', { params }),
  getById: (id) => api.get(`/customer-payments/${id}`),
  create: (data) => api.post('/customer-payments', data),
  update: (id, data) => api.put(`/customer-payments/${id}`, data),
  delete: (id) => api.delete(`/customer-payments/${id}`),
};

// Backup API
export const backupAPI = {
  create: () => api.post('/backup/create'),
  list: () => api.get('/backup/list'),
  status: () => api.get('/backup/status'),
  restore: (filename = null) => api.post('/backup/restore', { filename }),
  updateSettings: (settings) => api.put('/backup/settings', settings),
};

// Setup API
export const setupAPI = {
  check: () => api.get('/setup-auth/check-first-time'),
  migrate: () => api.post('/setup/migrate'),
  createAdmin: (data) => api.post('/setup-auth/create-admin', data),
};

// Authentication API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  /** Email OTP signup (backend + Microsoft Graph or SMTP) */
  zbSignupWithOtp: (data) => api.post('/auth/zb-signup-with-otp', data),
  completeSignupRole: (data) => api.post('/auth/complete-signup-role', data),
  /** Forgot password: OTP + new password (zb_simple_users + users) */
  zbResetPasswordAfterOtp: (data) => api.post('/auth/zb-reset-password-after-otp', data),
  /** Logged-in Zentrya user: current + new password (zb_simple_users + users) — requires API `DATABASE_URL` */
  zbChangePassword: (data) => api.post('/auth/zb-change-password', data),
  /** Zentrya email/password → POS `user_sessions` (may return requiresOtp) */
  zbSimpleSession: (data) => api.post('/auth/zb-simple-session', data),
  /** Completes MFA login — no session cookie required */
  zbSimpleSessionVerifyOtp: (data) => api.post('/auth/zb-simple-session/verify-otp', data),
  /** Google Sign-In (GIS id_token) */
  googleSignIn: (data) => api.post('/auth/google', data),
  googleSignInVerifyOtp: (data) => api.post('/auth/google/verify-otp', data),
  googleResendOtp: (data) => api.post('/auth/google/resend-otp', data),
  /** Email OTP MFA flag on zb_simple_users (requires POS API session + x-shop-id irrelevant) */
  zbEmailMfaGet: () => api.get('/auth/zb-email-mfa'),
  zbEmailMfaPut: (body) => api.put('/auth/zb-email-mfa', body),
  getStaffInvite: (token) => api.get(`/auth/staff-invite/${encodeURIComponent(token)}`),
  rejectStaffInvite: (token) => api.post(`/auth/staff-invite/${encodeURIComponent(token)}/reject`),
  acceptStaffInvite: (token, data) => api.post(`/auth/staff-invite/${encodeURIComponent(token)}/accept`, data),
  acceptStaffInviteExisting: (token) => api.post(`/auth/staff-invite/${encodeURIComponent(token)}/accept-existing`),
};

export const otpAPI = {
  request: (body) => api.post('/otp/request', body),
  verify: (body) => api.post('/otp/verify', body),
};

/** Stripe subscription checkout + customer portal (`backend/routes/billing.js`) */
export const billingAPI = {
  createCheckoutSession: (body) => api.post('/billing/create-checkout-session', body),
  createPortalSession: (body) => api.post('/billing/create-portal-session', body),
  syncSubscription: (body) => api.post('/billing/sync-subscription', body),
};

/** Shop picker (multi-shop stats) */
export const shopPickerAPI = {
  listShops: () => api.get('/shop-picker/my-shops'),
  planStatus: () => api.get('/shop-picker/plan-status'),
  currentShopPlan: () => api.get('/shop-picker/current-shop-plan'),
  checkShopAccess: (shopId) => api.get(`/shop-picker/shop-access/${encodeURIComponent(shopId)}`),
  shopPlanAccess: (shopIds) => api.post('/shop-picker/shop-plan-access', { shopIds }),
  quickStats: (shopIds) => api.post('/shop-picker/quick-stats', { shopIds }),
  createShop: (body) => api.post('/shop-picker/create-shop', body),
};

// Users API (Admin only)
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  sendInvitation: (data) => api.post('/users/invitations', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getAuditLogs: (params) => api.get('/users/audit-logs', { params }),
  generatePassword: (id) => api.post(`/users/${id}/generate-password`),
};

// Audit history (shop-scoped, admin)
export const auditAPI = {
  getLogs: (params) => api.get('/audit', { params }),
};

/** In-app support tickets (shop staff) */
export const supportTicketsAPI = {
  getAll: () => api.get('/support-tickets'),
  getById: (id) => api.get(`/support-tickets/${id}`),
  create: (body) => api.post('/support-tickets', body),
  getMessages: (id) => api.get(`/support-tickets/${id}/messages`),
  sendMessage: (id, body) => api.post(`/support-tickets/${id}/messages`, body),
  updateStatus: (id, status) => api.post(`/support-tickets/${id}/status`, { status }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
};

export default api;
